import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { AlertCategory } from '../../model/alerts'
import { PatientAlert, SeverityKeys } from '../../model/patient'
import { AlertsState } from '../../model/model'
import { CDS, CDS_RULE } from '../../model/cds'

const initialActiveAlerts = {
	LOW: {
		enabled: false,
		list: [],
	},
	MEDIUM: {
		enabled: false,
		list: [],
	},
	HIGH: {
		enabled: false,
		list: [],
	},
}

const initialState: AlertsState = {
	existingCategoryNames: [],
	list: [],
	availableCategories: [],
	activeAlerts: initialActiveAlerts,
	rules: [],
	uprise: [],
	cds: [],
	prevOpener: '',
	alertsPageIsOpened: false,
}

export const slice = createSlice({
	name: 'alerts',
	initialState,
	reducers: {
		setExistingCategoryNames: (state, { payload }: PayloadAction<string[]>) => {
			state.existingCategoryNames = payload
		},
		addNameToExisting: (state, { payload }: PayloadAction<string>) => {
			state.existingCategoryNames.push(payload)
		},
		setAlerts: (state, { payload }: PayloadAction<PatientAlert[]>) => {
			state.list = payload
		},
		setCDSRules: (state, { payload }: PayloadAction<CDS[]>) => {
			state.rules = payload
		},
		setUprisePatientAlerts: (
			state,
			{ payload }: PayloadAction<PatientAlert[]>,
		) => {
			state.uprise = payload
		},
		setCDSPatientAlert: (state, { payload }: PayloadAction<PatientAlert>) => {
			const index = state.cds.findIndex(
				({ category }) => category.id === payload.category.id,
			)
			if (index > -1) {
				state.cds[index] = payload
			} else {
				state.cds.push(payload)
			}
		},
		deleteCDSPatientAlert: (state, { payload }: PayloadAction<CDS_RULE>) => {
			const index = state.cds.findIndex(
				({ category }) => category.id === payload,
			)
			if (index > -1) {
				state.cds.splice(index, 1)
			}
		},
		setAvailableCategories: (
			state,
			{ payload }: PayloadAction<AlertCategory[]>,
		) => {
			state.availableCategories = payload
		},
		activateAlert: (
			state,
			{ payload }: PayloadAction<{ type: SeverityKeys; list: PatientAlert[] }>,
		) => {
			const { type, list } = payload

			state.activeAlerts[type].enabled = true
			state.activeAlerts[type].list = list
		},
		deactivateAlert: (state, { payload }: PayloadAction<SeverityKeys>) => {
			state.activeAlerts[payload].enabled = false
		},
		deactivateCDSAlert: (
			state,
			{ payload }: PayloadAction<{ type: SeverityKeys; rule: CDS_RULE }>,
		) => {
			const { type, rule } = payload
			const list = state.activeAlerts[type].list.filter(
				a => a.category.id !== rule,
			)

			if (!list.length) {
				state.activeAlerts[type].enabled = false
			}
			state.activeAlerts[type].list = list
		},
		resetActiveAlerts: state => {
			state.activeAlerts = initialActiveAlerts
		},
		resetCDSAlerts: (state, { payload }: PayloadAction<SeverityKeys>) => {
			const list = state.activeAlerts[payload].list.filter(
				a => a.type !== 'CDS',
			)

			state.activeAlerts[payload].list = list
		},
		clearCDSAlerts: state => {
			state.cds = []
		},
		setPrevOpener: (state, { payload }: PayloadAction<string>) => {
			state.prevOpener = payload
		},
		setAlertsPageIsOpened: (state, { payload }: PayloadAction<boolean>) => {
			state.alertsPageIsOpened = payload
		},
	},
})

export default slice.reducer
